import React, { memo, ReactNode, useState } from 'react';

import moment from 'moment';

import { useNavigate } from '@plasma/ui.application.router';
import Tooltip from '@plasma/ui.display.tooltip';
import Popconfirm from '@plasma/ui.feedback.pop-confirm';
import Button from '@plasma/ui.general.button/dist/button';
import Input from '@plasma/ui.input.input';
import { Select, Option } from '@plasma/ui.input.select';
import Modal from '@plasma/ui.layout.modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import LoadingComponent from '../../components/Loding-component/LoadingComponent';
import LoadingComponentScalable from '../../components/Loding-component/LoadingComponentMini';
import TopBarThemed from '../../components/top-bar-themed';
import { ProductDistribution } from '../../services/Interface/productionFigureInterface';
import useProductionFigureHook from './production-figure.hook';
import * as styled from './production-figure.styles';
import { NormalPlasmaButton, ProfileHeaderContainer, StyledCard, ProfileControls, RemainingTons } from './production-figure.styles';
import themes from './themes';

export const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const quarters = ["First Quarter", "Second Quarter", "Third Quarter", "Fourth Quarter"];

export interface ProductionFigureProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  size?: any;
  themeMode?: any;
}

type Props = ProductionFigureProps & ThemeOverride;

const ItemCard = memo(({
  distribution,
  index,
  productList,
  remainingTons,
  onAdd,
  isReadonly
}: {
  distribution: ProductDistribution;
  index: number;
  productList: any[];
  remainingTons: number;
  onAdd: (index: number, profileDim: string, tonnage: number) => void;
  isReadonly?: boolean;
}) => {
  const [profileDim, setProfileDim] = useState<string>('');
  const [profileTonnage, setProfileTonnage] = useState<number>(0);

  const currentSelectedProfiles = distribution.profileSelections?.map(s => s.profileDim) || [];
  const filteredDescriptions = productList
    .filter((res) => res.producttype === distribution.productType && !currentSelectedProfiles.includes(res.productdescription))
    .map((res) => res.productdescription);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const isProfileFilled = !!profileDim;
      const isTonnageFilled = profileTonnage > 0;

      if (!isProfileFilled) {
        const selectEl = document.getElementById(`profile-select-${index}`);
        (selectEl?.querySelector('input') as HTMLElement)?.focus() || selectEl?.focus();
      } else if (!isTonnageFilled) {
        document.getElementById(`tonnage-input-${index}`)?.focus();
      } else {
        const canAdd = !(
          isReadonly ||
          distribution.remainingPercentage <= 0 ||
          distribution.distributedValue <= 0 ||
          profileTonnage > remainingTons ||
          profileTonnage <= 0 ||
          !profileDim
        );

        if (canAdd) {
          onAdd(index, profileDim, profileTonnage);
          setProfileDim('');
          setProfileTonnage(0);

          setTimeout(() => {
            const selectEl = document.getElementById(`profile-select-${index}`);
            (selectEl?.querySelector('input') as HTMLElement)?.focus() || selectEl?.focus();
          }, 50);
        }
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} onKeyDown={handleKeyDown}>
      <styled.SelectStyled
        id={`profile-select-${index}`}
        placeholder="Profile"
        style={{ minWidth: '120px', flex: 1, borderRadius: '6px' }}
        value={profileDim}
        onChange={setProfileDim}
        allowClear
        showSearch
        disabled={isReadonly || distribution.remainingPercentage <= 0 || distribution.distributedValue <= 0}
      >
        {filteredDescriptions.map((desc, idx) => (
          <Option key={idx} value={desc}>{desc}</Option>
        ))}
      </styled.SelectStyled>

      <Input
        id={`tonnage-input-${index}`}
        allowClear
        style={{ width: '80px', marginLeft: '8px', padding: '4px', borderRadius: '6px' }}
        type="number"
        placeholder="(tons)"
        value={profileTonnage ? profileTonnage.toString() : ''}
        onChange={(e) => setProfileTonnage(Number(e.target.value))}
        disabled={isReadonly || distribution.remainingPercentage <= 0 || distribution.distributedValue <= 0}
        min={'0'}
      />

      <Tooltip title="Add" mouseEnterDelay={0.5}>
        <Button
          type="primary"
          htmlType="button"
          title="+"
          style={{ marginLeft: '8px', padding: '0 12px', fontSize: '18px', borderRadius: '6px' }}
          onClick={() => {
            onAdd(index, profileDim, profileTonnage);
            setProfileDim('');
            setProfileTonnage(0);
          }}
          disabled={
            isReadonly ||
            distribution.remainingPercentage <= 0 ||
            distribution.distributedValue <= 0 ||
            profileTonnage > remainingTons ||
            profileTonnage <= 0 ||
            !profileDim
          }
        />
      </Tooltip>
    </div>
  );
});

function ProductionFigure(props: Props) {
  const navigate = useNavigate();
  const { className, size, themeMode } = props;

  const {
    t, handlePlanNameChange, planNameError, handlePlanTypeChange,
    distributions, totalMatricTons, handleTotalMetricTonsChange, handleProfileValueChange,
    productList, existPlan, IsPlanCreate, selectedValue, handleSelectChange, planlist, planName,
    handleSaveButton, handleDeletePlanAndTemplate,
    handleCreateTemplate, handleCancel, suffixPlanName, setSuffixPlanName, getCurrentDateTime,
    handleNewPlanCreate, showplanSuffixModal, setShowplanSuffixModal, loading,
    showplanTemplateModal, handlePlanTemplateModalCancel, userRoles, isSuperUser,
    handleProfileAdd, handleProfileDelete, setExistPlan, isCalcButtonDisabled,
    isEditModalVisible, setIsEditModalVisible, editMode, handleEditPlan, handleCopyPlan, handleCalculationSnapshot,
    showManagePlanModal, setShowManagePlanModal
  } = useProductionFigureHook(props);

  const handleViewPlannedDemandClicked = () => {
    if (existPlan?.planId) {
      navigate(`/planned-demand-page/${existPlan.planId}/0/${existPlan.planName}`);
    }
  }

  const renderPlanDetailsAsLabels = () => {
    if (!existPlan) return null;

    const getDurationText = () => {
      switch (selectedValue) {
        case 'month': return <span style={{ color: '#0369a1', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{t('monthly')}</span>;
        case 'quater': return <span style={{ color: '#6d28d9', backgroundColor: '#ede9fe', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{t('quarterly')}</span>;
        case 'half_year': return <span style={{ color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{t('half-yearly')}</span>;
        case 'year': return <span style={{ color: '#047857', backgroundColor: '#d1fae5', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{t('yearly')}</span>;
        case 'date': return <span style={{ color: '#be185d', backgroundColor: '#fce7f3', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{t('custom-date-range')}</span>;
        default: return <span>{selectedValue}</span>;
      }
    };

    const getPeriodText = () => {
      switch (selectedValue) {
        case 'month': return <span style={{ color: '#0369a1', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{existPlan.plannedForYear} - {months[Number(existPlan.plannedForMonth) - 1]}</span>;
        case 'quater': return <span style={{ color: '#6d28d9', backgroundColor: '#ede9fe', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{existPlan.plannedForYear} - {quarters[Number(existPlan.plannedForQuarter) - 1]}</span>;
        case 'half_year': return <span style={{ color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{existPlan.plannedForYear} - {Number(existPlan.plannedForQuarter) === 1 ? t('first-half') : t('second-half')}</span>;
        case 'year': return <span style={{ color: '#047857', backgroundColor: '#d1fae5', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{existPlan.plannedForYear}</span>;
        case 'date': return <span style={{ color: '#be185d', backgroundColor: '#fce7f3', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{existPlan.planFromDate && existPlan.planToDate ? `${moment(existPlan.planFromDate).format('YYYY-MM-DD')} to ${moment(existPlan.planToDate).format('YYYY-MM-DD')}` : ''}</span>;
        default: return <span></span>;
      }
    };

    return (
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginLeft: '10px' }}>
        <div style={{ fontWeight: 'bold', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>{t("duration")}: {getDurationText()}</div>
        <div style={{ fontWeight: 'bold', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>{t("period")}: {getPeriodText()}</div>
      </div>
    );
  };

  const renderDateSelectionContent = () => {
    const currentYear = new Date().getFullYear();
    const dateFormat = 'YYYY-MM-DD';
    const timezone = 'UTC';
    const years = Array.from({ length: 11 }, (_, i) => (currentYear + i).toString());

    const customFormattDate = (value: any, field: string) => {
      const tzFormattedValue = value?.utc(timezone).format();
      const newExistPlan = { ...existPlan, [field]: tzFormattedValue } as any;
      handlePlanTypeChange('date', newExistPlan);
      setExistPlan({ ...newExistPlan });
    };

    const YearSelect = () => (
      <styled.SelectStyled
        placeholder="Select year"
        showSearch
        allowClear
        value={existPlan?.plannedForYear}
        onChange={(value) => setExistPlan((prevPlan: any) => ({ ...prevPlan, plannedForYear: value }))}
      >
        {years.map((year) => <Option key={year} value={year}>{year}</Option>)}
      </styled.SelectStyled>
    );

    switch (selectedValue) {
      case 'year':
        return <styled.DateSelectionContainer><YearSelect /></styled.DateSelectionContainer>;
      case 'month':
        return (
          <styled.DateSelectionContainer>
            <YearSelect />
            <styled.SelectStyled
              placeholder={t("select-month")}
              showSearch
              allowClear
              value={existPlan?.plannedForMonth?.toString()}
              onChange={(e) => setExistPlan((prevPlan: any) => ({ ...prevPlan, plannedForMonth: Number(e) }))}
            >
              {months.map((month, index) => <Option key={index + 1} value={(index + 1).toString()}>{month}</Option>)}
            </styled.SelectStyled>
          </styled.DateSelectionContainer>
        );
      case 'half_year':
        return (
          <styled.DateSelectionContainer>
            <YearSelect />
            <styled.SelectStyled
              showSearch
              allowClear
              placeholder={t("select-half-year")}
              value={existPlan?.plannedForQuarter?.toString()}
              onChange={(value) => setExistPlan((prevPlan: any) => ({ ...prevPlan, plannedForQuarter: Number(value) }))}
            >
              <Option key={'1'} value={'1'}>First half</Option>
              <Option key={'2'} value={'2'}>Second half</Option>
            </styled.SelectStyled>
          </styled.DateSelectionContainer>
        );
      case 'quater':
        return (
          <styled.DateSelectionContainer>
            <YearSelect />
            <styled.SelectStyled
              showSearch
              allowClear
              placeholder={t("select-quarter")}
              value={existPlan?.plannedForQuarter?.toString()}
              onChange={(value) => setExistPlan((prevPlan: any) => ({ ...prevPlan, plannedForQuarter: Number(value) }))}
            >
              {quarters.map((quarter, index) => <Option key={index + 1} value={(index + 1).toString()}>{quarter}</Option>)}
            </styled.SelectStyled>
          </styled.DateSelectionContainer>
        );
      case 'date':
        return (
          <styled.DatePickerContainer>
            <styled.DatePickerStyled
              placeholder={[t("start-date"), t("end-date")] as any}
              format={dateFormat}
              onChange={(date) => date && customFormattDate(date, "planFromDate")}
              value={existPlan?.planFromDate ? moment(existPlan.planFromDate) : null}
            />
            <styled.DatePickerStyled
              placeholder={t("end-date")}
              format={dateFormat}
              onChange={(date) => date && customFormattDate(date, "planToDate")}
              value={existPlan?.planToDate ? moment(existPlan.planToDate) : null}
            />
          </styled.DatePickerContainer>
        );
      default:
        return null;
    }
  };

  const renderContent = (isReadonly: boolean) => (
    <>
      <styled.PlanEditAndTotalContainer>
        <styled.PlanEditContainer>
          <styled.PlanEditLeft>
            <div style={{ textDecoration: "underline", marginLeft: "5px", fontWeight: 600}}>
              {(!IsPlanCreate && isReadonly) ? t("current-plan") + " :" : t("editing-plan") + " :"}
            </div>

            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              {!isReadonly && IsPlanCreate ? (
                <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                  <Input allowClear value={planName} style={{ fontWeight: 'bold', minWidth: '200px' }} title={t("plan-name")} type="text" placeholder={t("plan-name")} onChange={handlePlanNameChange} />
                  {planNameError && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{planNameError}</div>}
                </div>
              ) : (
                <Select disabled={!isReadonly} value={planName} onChange={handleSelectChange} placeholder={t('plan-name')} style={{minWidth: '200px'}} showSearch>
                  {planlist?.map((plan) => (
                    <Option key={plan.Planid} value={plan.Planname}>
                      {plan.Planname}
                    </Option>
                  ))}
                </Select>
              )}

              {isReadonly ? (
                <>
                  {renderPlanDetailsAsLabels()}
                  <NormalPlasmaButton
                    title={t("manage-plan")}
                    prefixIcon={{ name: 'edit' }}
                    onClick={() => setShowManagePlanModal(true)}
                  />
                </>
              ) : (
                <>
                  <styled.SelectStyled
                    style={{ minWidth: '150px' }}
                    showSearch
                    allowClear
                    value={selectedValue}
                    onChange={(value) => handlePlanTypeChange(value, existPlan as any)}
                    placeholder={t("duration")}
                  >
                    <Option key={0} value={'month'}>{t("monthly")}</Option>
                    <Option key={1} value={'quater'}>{t("quarterly")}</Option>
                    <Option key={2} value={'half_year'}>{t("half-yearly")}</Option>
                    <Option key={3} value={'year'}>{t("yearly")}</Option>
                    <Option key={4} value={'date'}>{t("date")}</Option>
                  </styled.SelectStyled>
                  <div>
                    {renderDateSelectionContent()}
                  </div>
                </>
              )}
            </div>
          </styled.PlanEditLeft>

          <styled.PlanEditRight>
            {isReadonly ? (
              <>
                {/* <NormalPlasmaButton
                  title={t("view-planned-demand")}
                  disabled={!existPlan}
                  onClick={handleViewPlannedDemandClicked}
                  prefixIcon={{name:'calculate'}}
                >
                  {t("view-planned-demand")}
                </NormalPlasmaButton> */}
              </>
            ) : (
              <>
                <NormalPlasmaButton title={t("save")} onClick={handleSaveButton} prefixIcon={{ name: 'save' }}>
                  {t("save")}
                </NormalPlasmaButton>
                <Button
                  type={'danger'}
                  htmlType={'button'}
                  title={t("cancel")}
                  onClick={handleCancel}
                  prefixIcon={{ name: 'close' }}
                >
                  {t("cancel")}
                </Button>
              </>
            )}
          </styled.PlanEditRight>
        </styled.PlanEditContainer>

        <styled.PlannedTotalContainer style={{ marginTop: '20px' }}>
          <StyledCard className="total-card" bordered={false} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
              <div style={{ textDecorationLine: 'underline', color: 'black', fontWeight: 'bold', fontSize: '16px' }}>
                {t("planned-total-production")}
              </div>

              <div style={{ display: 'flex', padding: '5px', alignItems: 'center', gap: '5px' }}>
                {(totalMatricTons || existPlan) ? (
                <>
                  <div style={{ color: 'black', fontWeight: '800', fontSize: '16px' }}>100% :</div>
                  <Input
                    allowClear
                    disabled={isReadonly}
                    style={{ fontSize: '18px', fontWeight: 'bold', padding: '5px', width: '150px' }}
                    title={t("total")}
                    value={Number(IsPlanCreate ? totalMatricTons : existPlan?.plannedTonnage).toFixed(0)}
                    type="number"
                    placeholder={t("enter-metric-tons")}
                    onChange={(e) => handleTotalMetricTonsChange(e.target.value, existPlan as any)}
                    min={'0'}
                  />
                </>
              ) : (
                <LoadingComponentScalable borderWidth={5} containerSize={75} initialSeconds={2}/>
              )}
              </div>
            </div>
          </StyledCard>
        </styled.PlannedTotalContainer>
      </styled.PlanEditAndTotalContainer>

      <styled.ProductGrid>
        {distributions.map((dist, index) => {
          const usedTonnage = dist.profileSelections?.reduce((sum, sel) => sum + ((dist.distributedValue * sel.profilePercent) / 100), 0) || 0;
          const remainingTons = dist.distributedValue - usedTonnage;

          return (
            <StyledCard key={index} bordered={false}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <ProfileHeaderContainer>{dist.productType}</ProfileHeaderContainer>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Input
                    allowClear
                    disabled={isReadonly}
                    value={dist.distributedValue ? Number(dist.distributedValue).toFixed(0) : 0}
                    type="number"
                    placeholder="%"
                    min={'0'}
                    style={{ width: '80px', padding: '4px', borderRadius: '6px', fontWeight: 'bold' }}
                    onChange={(e) => handleProfileValueChange(index, Math.max(0, Number(e.target.value)))}
                  />
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>T</div>
                  <div style={{ fontWeight: 'bold', color: '#1e293b', minWidth: '55px', textAlign: 'right' }}>
                    {dist.calPerCentDistribute ? Number(dist.calPerCentDistribute).toFixed(2) : '0.00'}%
                  </div>
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0 -20px 5px -20px' }} />

              <ProfileControls>
                <RemainingTons $isUsedUp={remainingTons <= 0}>
                  {remainingTons.toFixed(0)} {t("tons")} {t("remains")}
                </RemainingTons>
                {!isReadonly && (
                  <ItemCard
                    isReadonly={isReadonly}
                    distribution={dist}
                    index={index}
                    productList={productList}
                    remainingTons={remainingTons}
                    onAdd={handleProfileAdd}
                  />
                )}
              </ProfileControls>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '5px -20px 5px -20px' }} />

              <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                {dist.profileSelections?.map((selection, idx) => (
                  <styled.ProfileItemContainer key={idx}>
                    <div className="badges">
                      <div className="badge badge-dim">{selection.profileDim}</div>
                      <div className="badge badge-percent">
                        {selection.profilePercent.toFixed(0)}%
                      </div>
                      <div className="badge badge-tonnage">
                        {((dist.distributedValue * selection.profilePercent) / 100).toFixed(0)}T
                      </div>
                    </div>

                    {!isReadonly && (
                      <Button
                        disabled={isReadonly}
                        type={'text'}
                        htmlType={'button'}
                        prefixIcon={{ name: 'remove_circle_outline', style: { color: '#ef4444', fontSize: '18px' } }}
                        style={{ height: '30px', width: '30px', padding: 0 }}
                        onClick={() => handleProfileDelete(index, idx)}
                      />
                    )}
                  </styled.ProfileItemContainer>
                ))}
              </div>
            </StyledCard>
          );
        })}
      </styled.ProductGrid>
    </>
  );

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <styled.ProductionFigure className={`production-figure ${className ?? ''}`.trim()}>
        <TopBarThemed topHeading={t("production-plan")}>
          {loading && <LoadingComponent />}
        </TopBarThemed>

        {renderContent(true)}

        <Modal
          destroyOnClose
          title={editMode === 'new' ? t('create-new-plan') : editMode === 'copy' ? t('copy-plan') : t('edit-plan')}
          visible={isEditModalVisible}
          width={'95%'}
          style={{ top: '5vh' }}
          bodyStyle={{ height: 'calc(90vh - 55px)', overflowY: 'auto' }}
          onCancel={handleCancel}
          footer={null} // Actions are inside PlanEditRight
        >
          {renderContent(false)}
        </Modal>

        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {t("manage-plan")}
              {existPlan && <span style={{ color: '#0284c7' }}>{planName}</span>}
            </div>
          }
          visible={showManagePlanModal}
          width="400px"
          onCancel={() => setShowManagePlanModal(false)}
          footer={null}
          destroyOnClose
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '15px' }}>
            <NormalPlasmaButton style={{ width: '100%', justifyContent: 'center', fontSize: '16px', minHeight: '44px' }} title={t("create-a-new-plan")} onClick={() => { setShowManagePlanModal(false); handleNewPlanCreate(); }} prefixIcon={{ name: 'add_circle' }}>
              {t("create-a-new-plan")}
            </NormalPlasmaButton>
            <NormalPlasmaButton disabled={planlist.length === 0 || !existPlan} style={{ width: '100%', justifyContent: 'center', fontSize: '16px', minHeight: '44px' }} title={`${t("edit-plan")}`} onClick={() => { setShowManagePlanModal(false); handleEditPlan(); }} prefixIcon={{ name: 'edit' }}>
              {t("edit-plan")}
              {/* {existPlan && <strong style={{marginLeft: '4px', color: '#0284c7'}}>{planName}</strong>} */}
            </NormalPlasmaButton>
            <NormalPlasmaButton disabled={planlist.length === 0 || !existPlan} style={{ width: '100%', justifyContent: 'center', fontSize: '16px', minHeight: '44px' }} title={`${t("copy-plan")}`} onClick={() => { setShowManagePlanModal(false); handleCopyPlan(); }} prefixIcon={{ name: 'content_copy' }}>
              {t("copy-plan")}
              {/* {existPlan && <strong style={{marginLeft: '4px', color: '#0284c7'}}>{planName}</strong>} */}
            </NormalPlasmaButton>
            <NormalPlasmaButton title={t("view-planned-demand")} disabled={planlist.length === 0 || !existPlan} onClick={handleViewPlannedDemandClicked} prefixIcon={{name:'calculate'}}>
              {t("view-planned-demand")}
            </NormalPlasmaButton>
            <NormalPlasmaButton disabled={planlist.length === 0 || !existPlan || isCalcButtonDisabled} style={{ width: '100%', justifyContent: 'center', fontSize: '16px', minHeight: '44px' }} title={t("create-new-calculation")} onClick={handleCalculationSnapshot} prefixIcon={{ name: 'calculate' }}>
              {t("create-new-calculation")}
            </NormalPlasmaButton>
            {(userRoles.includes("Ts-Admin") || isSuperUser) && (
              <Popconfirm
                title={t("delete-template-and-all-calculations")}
                onConfirm={() => { setShowManagePlanModal(false); handleDeletePlanAndTemplate(); }}
                onCancel={() => {}}
                okText={t("yes")}
                cancelText={t("no")}
              >
                <Button
                  title={t("delete-template-and-all-calculations")}
                  type={'primary'}
                  htmlType={'button'}
                  disabled={planlist.length === 0 || !existPlan}
                  prefixIcon={{name:'delete'}}
                  style={{ width: '100%', justifyContent: 'center', fontSize: '16px', minHeight: '44px' }}
                >
                  {t("delete-template-and-all-calculations")}
                </Button>
              </Popconfirm>
            )}
          </div>
        </Modal>

        <Modal
          destroyOnClose
          title={t('new-plan-template')}
          width={'fit-content'}
          visible={showplanTemplateModal}
          onCancel={() => handlePlanTemplateModalCancel()}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              {/* <NormalPlasmaButton title={t("save-template")} onClick={() => handleSubmit('')} prefixIcon={{ name: 'save' }} /> */}
              <Button type='secondary' title={t("cancel")} onClick={() => handlePlanTemplateModalCancel()} prefixIcon={{ name: 'close' }} htmlType={'button'}>
                {t("cancel")}
              </Button>
            </div>
          }
        >
          Would you like to save the plan as a template or a new snapshot?
        </Modal>

        <Modal
          destroyOnClose
          title={t("calculation-snapshot-suffix")}
          visible={showplanSuffixModal}
          onOk = {() => handleCreateTemplate(suffixPlanName)}
          onCancel={() => {setSuffixPlanName(getCurrentDateTime()); setShowplanSuffixModal(false);}}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}> | </span>
            <Input
              placeholder={t('enter-plan-name-suffix')}
              allowClear
              value={suffixPlanName}
              onChange={(e) => setSuffixPlanName(e.target.value)}
            />
          </div>
        </Modal>
      </styled.ProductionFigure>
    </ThemeProvider>
  );
}

export default memo(ProductionFigure) as unknown as typeof ProductionFigure;
